import React from 'react';
import { Grid, Card, CardContent, Box, Typography, Chip, Avatar, LinearProgress } from '@mui/material';
import {
  CheckCircle as CompletedIcon,
  PlayArrow as InProgressIcon,
  Lock as LockedIcon,
  Timeline as TimelineIcon
} from '@mui/icons-material';

const TopicCard = ({ topic, status, progress, onTopicClick }) => {
  const getStatusIcon = () => {
    switch (status) {
      case 'completed':
        return <CompletedIcon color="success" />;
      case 'in_progress':
        return <InProgressIcon color="primary" />;
      case 'locked':
        return <LockedIcon color="disabled" />;
      default:
        return <TimelineIcon color="action" />;
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'completed':
        return '#4CAF50';
      case 'in_progress':
        return '#2196F3';
      case 'locked':
        return '#9E9E9E';
      default:
        return '#757575';
    }
  };

  const getStatusLabel = () => {
    switch (status) {
      case 'completed':
        return 'Completed';
      case 'in_progress':
        return 'In Progress';
      case 'locked':
        return 'Locked';
      default:
        return 'Not Started';
    }
  };

  return (
    <Card
      sx={{
        cursor: status !== 'locked' ? 'pointer' : 'not-allowed',
        transition: 'all 0.3s ease',
        opacity: status === 'locked' ? 0.6 : 1,
        borderRadius: '16px',
        '&:hover': status !== 'locked' ? {
          transform: 'translateY(-4px)',
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
        } : {},
        position: 'relative',
        overflow: 'hidden',
        border: '1px solid',
        borderColor: status === 'in_progress' ? 'primary.main' : 'divider',
        boxShadow: 'none'
      }}
      onClick={() => status !== 'locked' && onTopicClick(topic)}
    >
      {/* Progress overlay */}
      {progress > 0 && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 4,
            backgroundColor: 'rgba(0,0,0,0.05)',
            '&::after': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              height: '100%',
              width: `${progress}%`,
              backgroundColor: getStatusColor(),
              transition: 'width 0.3s ease'
            }
          }}
        />
      )}

      <CardContent sx={{ pt: progress > 0 ? 4 : 3 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Box display="flex" alignItems="center" flex={1}>
            <Avatar
              sx={{
                bgcolor: topic.status === 'completed' ? '#4CAF50' : '#2D5BFF',
                width: 48,
                height: 48,
                mr: 2.5,
                borderRadius: '16px',
                boxShadow: topic.status === 'completed' ? 'none' : '0 4px 12px rgba(45, 91, 255, 0.2)'
              }}
            >
              <TimelineIcon />
            </Avatar>
            <Box flex={1}>
              <Typography variant="h6" fontWeight={600} sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {topic.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {topic.description || 'Master key concepts and problem-solving techniques'}
              </Typography>
              {/* Subtopics list */}
              {topic.subtopics && topic.subtopics.length > 0 && (
                <Box mt={1.5} display="flex" flexWrap="wrap" gap={1}>
                  {topic.subtopics.slice(0, 3).map((st) => (
                    <Chip
                      key={st.id}
                      label={st.name}
                      size="small"
                      variant="outlined"
                        sx={{ borderRadius: '16px', fontSize: '0.65rem', height: 20, fontWeight: 700 }}
                    />
                  ))}
                  {topic.subtopics.length > 3 && (
                    <Typography variant="caption" color="text.secondary" sx={{ pt: 0.2 }}>+{topic.subtopics.length - 3} more</Typography>
                  )}
                </Box>
              )}
            </Box>
          </Box>
          {getStatusIcon()}
        </Box>

        {progress > 0 && (
          <Box mb={2}>
            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography variant="caption" color="text.secondary">
                Progress
              </Typography>
              <Typography variant="caption" fontWeight={600}>
                {progress}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={topic.progress || 0}
              sx={{
                height: 8,
                borderRadius: '16px',
                bgcolor: 'rgba(45, 91, 255, 0.05)',
                '& .MuiLinearProgress-bar': {
                  bgcolor: '#2D5BFF'
                }
              }}
            />
          </Box>
        )}

        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Chip
            label={getStatusLabel()}
            size="small"
            sx={{
              bgcolor: getStatusColor() + '15',
              color: getStatusColor(),
              fontWeight: 800,
              borderRadius: '16px',
              letterSpacing: '0.02em',
              textTransform: 'uppercase',
              fontSize: '0.65rem'
            }}
          />
          <Typography variant="caption" color="text.secondary">
            {(() => {
              const conceptCount = topic.subtopics ? topic.subtopics.reduce((sum, s) => sum + (s.concepts?.length || 0), 0) : (topic.conceptCount || 0);
              return (conceptCount || Math.floor(Math.random() * 8) + 3) + ' concepts';
            })()}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

const TopicGrid = ({ topics, onTopicClick, loading }) => {
  if (loading) {
    return (
      <Grid container spacing={3}>
        {[...Array(6)].map((_, i) => (
          <Grid item xs={12} md={6} key={i}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <Avatar sx={{ width: 40, height: 40, mr: 2 }}>
                    <TimelineIcon />
                  </Avatar>
                  <Box flex={1}>
                    <Typography variant="h6">Loading topic...</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Please wait
                    </Typography>
                  </Box>
                </Box>
                <LinearProgress sx={{ height: 6 }} />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  }

  if (!topics?.length) {
    return (
      <Box textAlign="center" py={8}>
        <TimelineIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No topics available
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Topics will be added to this subject soon.
        </Typography>
      </Box>
    );
  }

  return (
    <Grid container spacing={3}>
      {topics.map((topic, index) => {
        // Determine status from topic properties
        let status = 'not_started';
        if (topic.completed) status = 'completed';
        else if (topic.inProgress) status = 'in_progress';
        else if (topic.locked) status = 'locked';

        const progress = topic.progress || 0;

        return (
          <Grid item xs={12} md={6} key={topic.id || index}>
            <TopicCard
              topic={topic}
              status={status}
              progress={progress}
              onTopicClick={onTopicClick}
            />
          </Grid>
        );
      })}
    </Grid>
  );
};

export default TopicGrid;